import React from 'react';
import ReactDomClient from 'react-dom/client';
import {compose} from 'redux';

import AppStateHOC from '../lib/app-state-hoc.jsx';
import GUI from '../containers/gui.jsx';
import HashParserHOC from '../lib/hash-parser-hoc.jsx';
import log from '../lib/log.js';
import {detectMcRemoteConnectionTargetRoute} from '../lib/mcremote-connection-target-persistence.js';
import {getMcRemoteConnectionTargetByRoute} from '../lib/mcremote-connection-targets.js';
import {getMcRemoteRuntimeConfig} from '../lib/mcremote-runtime-config.js';
import {PLATFORM} from '../lib/platform.js';

const onClickLogo = () => {
    window.location = 'https://scratch.mit.edu';
};

// Allow auto-loading an extension via the `?extension=<id>` URL param, e.g.
// `?extension=mcremote`. Builtin extensions load synchronously here.
const extensionMatches = window.location.href.match(/[?&]extension=([^&]+)/);
const autoLoadExtensionId = extensionMatches ? decodeURIComponent(extensionMatches[1]) : null;

const logExtensionLoadFailure = function (error) {
    log(`Failed to auto-load extension "${autoLoadExtensionId}": ${error}`);
};

const handleVmInit = function (vm) {
    // Showcase builds ship the blocks but no connectivity. This is fixed at build time and cannot
    // be undone by the runtime config below, so a mistake in one does not enable the connection.
    if (process.env.MCREMOTE_SHOWCASE) {
        vm.disableMcRemoteConnection();
    }
    vm.setMcRemoteRuntimeConfig(getMcRemoteRuntimeConfig());
    const connectionTarget = getMcRemoteConnectionTargetByRoute(detectMcRemoteConnectionTargetRoute());
    vm.setMcRemoteConnectionTarget({
        sandboxRoute: connectionTarget.sandboxRoute,
        label: connectionTarget.label
    });
    if (autoLoadExtensionId) {
        vm.extensionManager.loadExtensionURL(autoLoadExtensionId).catch(logExtensionLoadFailure);
    }
};

export {handleVmInit};

const handleTelemetryModalCancel = () => {
    log('User canceled telemetry modal');
};

const handleTelemetryModalOptIn = () => {
    log('User opted into telemetry');
};

const handleTelemetryModalOptOut = () => {
    log('User opted out of telemetry');
};

/*
 * Render the GUI playground. This is a separate function because importing anything
 * that instantiates the VM causes unsupported browsers to crash
 * {object} appTarget - the DOM element to render to
 */
const renderGUI = function (appTarget) {
    GUI.setAppElement(appTarget);

    // note that redux's 'compose' function is just being used as a general utility to make
    // the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
    // ability to compose reducers.
    const WrappedGui = compose(
        AppStateHOC,
        HashParserHOC
    )(GUI);

    // TODO a hack for testing the backpack, allow backpack host to be set by url param
    const backpackHostMatches = window.location.href.match(/[?&]backpack_host=([^&]*)&?/);
    const backpackHost = backpackHostMatches ? backpackHostMatches[1] : null;

    const scratchDesktopMatches = window.location.href.match(/[?&]isScratchDesktop=([^&]+)/);
    let simulateScratchDesktop;
    if (scratchDesktopMatches) {
        try {
            // parse 'true' into `true`, 'false' into `false`, etc.
            simulateScratchDesktop = JSON.parse(scratchDesktopMatches[1]);
        } catch {
            // it's not JSON so just use the string
            // note that a typo like "falsy" will be treated as true
            simulateScratchDesktop = scratchDesktopMatches[1];
        }
    }

    if (process.env.NODE_ENV === 'production' && typeof window === 'object') {
        // Warn before navigating away
        window.onbeforeunload = () => true;
    }

    const root = ReactDomClient.createRoot(appTarget);

    root.render(
        // important: this is checking whether `simulateScratchDesktop` is truthy, not just defined!
        simulateScratchDesktop ?
            <WrappedGui
                canEditTitle
                platform={PLATFORM.DESKTOP}
                showTelemetryModal
                canSave={false}
                onVmInit={handleVmInit}
                onTelemetryModalCancel={handleTelemetryModalCancel}
                onTelemetryModalOptIn={handleTelemetryModalOptIn}
                onTelemetryModalOptOut={handleTelemetryModalOptOut}
            /> :
            <WrappedGui
                canEditTitle
                backpackVisible
                showComingSoon
                backpackHost={backpackHost}
                canSave={false}
                onVmInit={handleVmInit}
                onClickLogo={onClickLogo}
            />
    );
};

export default renderGUI;
